import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Check, ChevronRight } from "lucide-react";

const FREQ = ["Daily", "Weekly", "Fortnightly", "Monthly", "Quarterly", "Six Monthly", "Annual"];
const today = () => new Date().toISOString().slice(0, 10);

function Text({ label, value, onChange, type = "text", required, testid, placeholder, disabled }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}{required && " *"}</Label>
      <Input data-testid={testid} type={type} value={value || ""} disabled={disabled} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="mt-1 bg-white" />
    </div>
  );
}

function Area({ label, value, onChange, testid }) {
  return (
    <div className="md:col-span-2">
      <Label className="text-xs text-slate-600">{label}</Label>
      <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" rows={2} />
    </div>
  );
}

export default function OnboardingWizard({ open, onClose, onComplete, mode = "full", presetCustomer }) {
  const isSite = mode === "site";
  const stepLabels = isSite ? ["Site", "First Job"] : ["Customer", "Site", "First Job"];
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [techs, setTechs] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [co, setCo] = useState({});
  const [contracts, setContracts] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState({});
  const [createdSite, setCreatedSite] = useState(null);
  const [job, setJob] = useState({ kind: "oneoff" });

  useEffect(() => {
    if (!open) return;
    setStep(0); setSaving(false);
    setCo({ payment_terms: "30 days", po_required: false });
    setContracts([{ name: "", contract_value: "", contract_type: "Recurring" }]);
    setCustomer(presetCustomer || null);
    setSite({}); setCreatedSite(null);
    setJob({ kind: "oneoff", priority: "Medium" });
    api.get("/technicians").then((r) => setTechs(r.data)).catch(() => {});
    if (isSite) api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, [open, isSite, presetCustomer]);

  const setCoK = (k, v) => setCo((p) => ({ ...p, [k]: v }));
  const setSiteK = (k, v) => setSite((p) => ({ ...p, [k]: v }));
  const setJobK = (k, v) => setJob((p) => ({ ...p, [k]: v }));

  const addContract = () => setContracts((p) => [...p, { name: "", contract_value: "", contract_type: "Recurring" }]);
  const removeContract = (i) => setContracts((p) => p.filter((_, idx) => idx !== i));
  const setContractK = (i, k, v) => setContracts((p) => p.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));

  const showCustomer = !isSite && step === 0;
  const showSite = isSite ? step === 0 : step === 1;
  const showJob = isSite ? step === 1 : step === 2;

  const saveCustomer = async () => {
    if (!co.name) { toast.error("Legal customer name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/customers", {
        name: co.name, legal_name: co.name, status: "Active",
        main_contact: co.main_contact, email: co.email, phone: co.phone,
        billing_address: co.billing_address,
        accounts_contact: co.accounts_contact, accounts_email: co.accounts_email, accounts_phone: co.accounts_phone,
        payment_terms: co.payment_terms, po_required: !!co.po_required, notes: co.notes,
      });
      setCustomer({ id: data.id, name: data.name });
      let made = 0;
      for (const c of contracts) {
        if (!c.name && !c.contract_value) continue;
        await api.post("/contracts", {
          name: c.name || `${data.name} contract`, customer_id: data.id, customer_name: data.name,
          contract_value: parseFloat(c.contract_value) || 0, contract_type: c.contract_type,
        });
        made += 1;
      }
      toast.success(`Customer ${data.ref} created${made ? ` · ${made} contract(s)` : ""}`);
      setStep(1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create customer"); }
    finally { setSaving(false); }
  };

  const saveSite = async () => {
    const custId = isSite ? site.customer_id : customer?.id;
    const custName = isSite ? customers.find((c) => c.id === site.customer_id)?.name : customer?.name;
    if (isSite && !custId) { toast.error("Select a customer"); return; }
    if (!site.name) { toast.error("Site name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/sites", {
        name: site.name, customer_id: custId, customer_name: custName,
        site_contact: site.site_contact, site_email: site.site_email, site_phone: site.site_phone,
        address: site.address, site_notes: site.site_notes,
      });
      setCreatedSite({ id: data.id, name: data.name });
      if (isSite) setCustomer({ id: custId, name: custName });
      setJob((p) => ({ ...p, title: `Initial visit — ${data.name}` }));
      toast.success(`Site ${data.ref} created`);
      setStep(isSite ? 1 : 2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create site"); }
    finally { setSaving(false); }
  };

  const saveJob = async () => {
    if (!job.title) { toast.error("Job title is required"); return; }
    const tech = techs.find((t) => t.id === job.technician_id);
    setSaving(true);
    try {
      if (job.kind === "recurring") {
        const { data: tpl } = await api.post("/job-templates", {
          title: job.title, service_type: job.job_type || "Planned preventative maintenance",
          customer_id: customer?.id, customer_name: customer?.name,
          site_id: createdSite?.id, site_name: createdSite?.name,
          frequency: job.frequency || "Monthly", start_date: job.scheduled_date || today(),
          preferred_time: job.scheduled_time, estimated_duration: job.estimated_duration,
          priority: "Normal", technician_id: job.technician_id, technician_name: tech?.name,
        });
        const { data: gen } = await api.post(`/job-templates/${tpl.id}/generate`);
        toast.success(`Recurring work created · ${gen.created} visit(s) generated`);
      } else {
        await api.post("/jobs", {
          title: job.title, job_type: job.job_type || "Reactive call-out",
          customer_id: customer?.id, customer_name: customer?.name,
          site_id: createdSite?.id, site_name: createdSite?.name,
          scheduled_date: job.scheduled_date, scheduled_time: job.scheduled_time,
          estimated_duration: job.estimated_duration, priority: job.priority || "Medium",
          technician_id: job.technician_id, technician_name: tech?.name,
          operational_status: job.technician_id ? "Assigned" : "Scheduled",
        });
        toast.success("Job created — ready for scheduling & operations");
      }
      onComplete && onComplete();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create job"); }
    finally { setSaving(false); }
  };

  const kindBtn = (active) => `flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${active ? "btn-accent border-transparent" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">{isSite ? "Add New Site" : "Add New Customer"}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap">
          {stepLabels.map((l, i) => (
            <div key={l} className="flex items-center gap-2" data-testid={`wiz-step-${i}`}>
              <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-semibold ${i < step ? "bg-emerald-500 text-white" : i === step ? "text-white" : "bg-slate-200 text-slate-500"}`}
                style={i === step ? { backgroundColor: "var(--client-accent)" } : {}}>
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`text-sm ${i === step ? "font-semibold text-slate-900" : "text-slate-400"}`}>{l}</span>
              {i < stepLabels.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
            </div>
          ))}
        </div>

        {/* Customer step */}
        {showCustomer && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Text label="Legal customer name" required testid="wiz-cust-name" value={co.name} onChange={(v) => setCoK("name", v)} />
              <Text label="Customer account reference" disabled testid="wiz-cust-ref" value="" placeholder="Auto-generated on save" onChange={() => {}} />
              <Text label="Main contact name" testid="wiz-cust-main" value={co.main_contact} onChange={(v) => setCoK("main_contact", v)} />
              <Text label="Main contact email" testid="wiz-cust-email" value={co.email} onChange={(v) => setCoK("email", v)} />
              <Text label="Main contact telephone" testid="wiz-cust-phone" value={co.phone} onChange={(v) => setCoK("phone", v)} />
              <Text label="Payment terms" testid="wiz-cust-terms" value={co.payment_terms} onChange={(v) => setCoK("payment_terms", v)} />
              <Area label="Billing address" testid="wiz-cust-billing" value={co.billing_address} onChange={(v) => setCoK("billing_address", v)} />
              <Text label="Accounts contact name" testid="wiz-cust-acc-name" value={co.accounts_contact} onChange={(v) => setCoK("accounts_contact", v)} />
              <Text label="Accounts email" testid="wiz-cust-acc-email" value={co.accounts_email} onChange={(v) => setCoK("accounts_email", v)} />
              <Text label="Accounts telephone" testid="wiz-cust-acc-phone" value={co.accounts_phone} onChange={(v) => setCoK("accounts_phone", v)} />
              <div className="flex items-center gap-3 pt-5">
                <Switch data-testid="wiz-cust-po" checked={!!co.po_required} onCheckedChange={(v) => setCoK("po_required", v)} />
                <Label className="text-sm text-slate-600">Purchase order required</Label>
              </div>
              <Area label="Notes" testid="wiz-cust-notes" value={co.notes} onChange={(v) => setCoK("notes", v)} />
            </div>

            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-slate-700">Contract details</Label>
                <Button type="button" variant="outline" size="sm" data-testid="wiz-add-contract" onClick={addContract}><Plus className="h-4 w-4 mr-1" />Add contract</Button>
              </div>
              <div className="space-y-2">
                {contracts.map((c, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-slate-50 border border-slate-200 rounded-md p-2" data-testid={`wiz-contract-${i}`}>
                    <div className="md:col-span-5">
                      <Label className="text-xs text-slate-600">Contract name (optional)</Label>
                      <Input data-testid={`wiz-contract-name-${i}`} value={c.name} onChange={(e) => setContractK(i, "name", e.target.value)} className="mt-1 bg-white" />
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs text-slate-600">Contract value</Label>
                      <Input data-testid={`wiz-contract-value-${i}`} type="number" value={c.contract_value} onChange={(e) => setContractK(i, "contract_value", e.target.value)} className="mt-1 bg-white" />
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs text-slate-600">Contract type</Label>
                      <Select value={c.contract_type} onValueChange={(v) => setContractK(i, "contract_type", v)}>
                        <SelectTrigger data-testid={`wiz-contract-type-${i}`} className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white"><SelectItem value="One-Off">One-Off</SelectItem><SelectItem value="Recurring">Recurring</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-1">
                      {contracts.length > 1 && <Button type="button" variant="ghost" size="icon" data-testid={`wiz-contract-remove-${i}`} onClick={() => removeContract(i)}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Site step */}
        {showSite && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isSite && (
              <div className="md:col-span-2">
                <Label className="text-xs text-slate-600">Customer *</Label>
                <Select value={site.customer_id || ""} onValueChange={(v) => setSiteK("customer_id", v)}>
                  <SelectTrigger data-testid="wiz-site-customer" className="mt-1 bg-white"><SelectValue placeholder="Select existing customer" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {!isSite && customer && <div className="md:col-span-2 text-xs bg-slate-50 border border-slate-200 rounded-md p-2 text-slate-500">Adding site for <b className="text-slate-700">{customer.name}</b></div>}
            <Text label="Site name" required testid="wiz-site-name" value={site.name} onChange={(v) => setSiteK("name", v)} />
            <Text label="Site contact name" testid="wiz-site-contact" value={site.site_contact} onChange={(v) => setSiteK("site_contact", v)} />
            <Text label="Site contact email" testid="wiz-site-email" value={site.site_email} onChange={(v) => setSiteK("site_email", v)} />
            <Text label="Site contact telephone" testid="wiz-site-phone" value={site.site_phone} onChange={(v) => setSiteK("site_phone", v)} />
            <Area label="Site address" testid="wiz-site-address" value={site.address} onChange={(v) => setSiteK("address", v)} />
            <Area label="Site notes / details" testid="wiz-site-notes" value={site.site_notes} onChange={(v) => setSiteK("site_notes", v)} />
          </div>
        )}

        {/* Job step */}
        {showJob && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button type="button" data-testid="wiz-job-oneoff" onClick={() => setJobK("kind", "oneoff")} className={kindBtn(job.kind === "oneoff")}>One-Off Job</button>
              <button type="button" data-testid="wiz-job-recurring" onClick={() => setJobK("kind", "recurring")} className={kindBtn(job.kind === "recurring")}>Recurring Job</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Text label="Job title" required testid="wiz-job-title" value={job.title} onChange={(v) => setJobK("title", v)} /></div>
              {job.kind === "recurring" ? (
                <>
                  <div>
                    <Label className="text-xs text-slate-600">Recurring schedule</Label>
                    <Select value={job.frequency || "Monthly"} onValueChange={(v) => setJobK("frequency", v)}>
                      <SelectTrigger data-testid="wiz-job-frequency" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Text label="Start date" type="date" testid="wiz-job-start" value={job.scheduled_date} onChange={(v) => setJobK("scheduled_date", v)} />
                  <Text label="Preferred time" testid="wiz-job-time" value={job.scheduled_time} placeholder="09:00" onChange={(v) => setJobK("scheduled_time", v)} />
                  <Text label="Estimated duration per visit (hours)" type="number" testid="wiz-job-duration" value={job.estimated_duration} onChange={(v) => setJobK("estimated_duration", v)} />
                </>
              ) : (
                <>
                  <Text label="Scheduled date" type="date" testid="wiz-job-date" value={job.scheduled_date} onChange={(v) => setJobK("scheduled_date", v)} />
                  <Text label="Scheduled time" testid="wiz-job-time" value={job.scheduled_time} placeholder="09:00" onChange={(v) => setJobK("scheduled_time", v)} />
                  <Text label="Estimated duration (hours)" type="number" testid="wiz-job-duration" value={job.estimated_duration} onChange={(v) => setJobK("estimated_duration", v)} />
                </>
              )}
              <div>
                <Label className="text-xs text-slate-600">Assign engineer</Label>
                <Select value={job.technician_id || ""} onValueChange={(v) => setJobK("technician_id", v)}>
                  <SelectTrigger data-testid="wiz-job-engineer" className="mt-1 bg-white"><SelectValue placeholder="Select engineer" /></SelectTrigger>
                  <SelectContent className="bg-white">{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-400">Status will be set to {job.technician_id ? "Assigned" : "Scheduled"} · linked to {customer?.name || "customer"} / {createdSite?.name || "site"}.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {showCustomer && <Button className="btn-accent" data-testid="wiz-next-customer" onClick={saveCustomer} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save &amp; Add Site</Button>}
          {showSite && <Button className="btn-accent" data-testid="wiz-next-site" onClick={saveSite} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save &amp; Add Job</Button>}
          {showJob && <Button className="btn-accent" data-testid="wiz-create-job" onClick={saveJob} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{job.kind === "recurring" ? "Create Recurring Work" : "Create Job"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
